import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EventFormData, VenueData } from './types';
import ReactQuill from 'react-quill';
import { getAllCountries, getAllCategories } from '../../../api';
import Select from "react-select";
import { useFormik } from 'formik';
import { eventDetailsValidationSchema, getFieldError, isFieldInvalid } from './validationSchemas';
import VenueModal from './Components/VenueModal';
import VenueList from './Components/VenueList';
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import ImageCropper from '../../../components/common/ImageCropper/ImageCropper';
import CustomDatePicker from '../../../components/common/CustomDatePicker/CustomDatePicker';
import CustomTimePicker from '../../../components/common/CustomTimePicker/CustomTimePicker';
import { FileText, Image as ImageIcon, MapPin, Folder } from 'lucide-react';

interface EventDetailsTabProps {
  formData: EventFormData;
  onInputChange: (field: keyof EventFormData, value: any) => void;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  activeTab: number;
  onValidationChange?: (isValid: boolean) => void;
}

const EventDetailsTab: React.FC<EventDetailsTabProps> = ({
  formData,
  onInputChange,
  onImageUpload,
  activeTab,
  onValidationChange
}) => {
  const [countries, setCountries] = useState([]);
  const [isVenueModalOpen, setIsVenueModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<VenueData | undefined>(undefined);
  const [categoriesList, setCategoriesList] = useState<any[]>([]);

  useEffect(() => {
    const getCategories = async () => {
      try {
        const response = await getAllCategories();
        setCategoriesList(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getCategories();
  }, []);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const formik = useFormik({
    initialValues: {
      eventName: formData.eventName || '',
      eventDate: formData.eventDate || '',
      endDate: formData.endDate || '',
      eventTime: formData.eventTime || '',
      eventEndTime: formData.eventEndTime || '',
      isSingleDayEvent: formData.isSingleDayEvent || false,
      eventImage: formData.eventImage || null,
      eventDescription: formData.eventDescription || '',
      shortDescription: formData.shortDescription || '',
      venue: formData.venue || '',
      venueDetails: formData.venueDetails || {},
      categories: formData.categories || [],
      duration: formData.duration || '',
      hasMultipleVenues: formData.hasMultipleVenues || false,
      venues: formData.venues || [],
    },
    validationSchema: eventDetailsValidationSchema,
    enableReinitialize: true,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(formik.isValid && Object.keys(formik.touched).length > 0);
    }
  }, [formik.isValid, formik.touched, onValidationChange]);

  useEffect(() => {
    formik.setValues({
      eventName: formData.eventName || '',
      eventDate: formData.eventDate || '',
      endDate: formData.endDate || '',
      eventTime: formData.eventTime || '',
      eventEndTime: formData.eventEndTime || '',
      isSingleDayEvent: formData.isSingleDayEvent || false,
      eventImage: formData.eventImage || null,
      eventDescription: formData.eventDescription || '',
      shortDescription: formData.shortDescription || '',
      venue: formData.venue || '',
      venueDetails: formData.venueDetails || {},
      categories: formData.categories || [],
      duration: formData.duration || '',
      hasMultipleVenues: formData.hasMultipleVenues || false,
      venues: formData.venues || [],
    });
  }, [formData]);

  const handleInputChange = (field: keyof EventFormData, value: any) => {
    formik.setFieldValue(field, value);
    onInputChange(field, value);
  };

  const handleSingleVenueLocationChange = (location: {
    name?: string;
    latitude?: number;
    longitude?: number;
    formattedAddress?: string;
    addressLine1?: string;
    addressLine2?: string;
    country?: string;
    state?: string;
    city?: string;
    zipCode?: string;
  }) => {
    const venueDetails = {
      ...formData.venueDetails,
      name: location.name || location.formattedAddress || '',
      addressLine1: location.formattedAddress || '',
      addressLine2: location.addressLine2 || '',
      country: location.country || '',
      state: location.state || '',
      city: location.city || '',
      zipCode: location.zipCode || '',
      latitude: location.latitude,
      longitude: location.longitude,
      formattedAddress: location.formattedAddress || '',
    };
    handleInputChange('venueDetails', venueDetails);
    handleInputChange('eventLocation', location.formattedAddress || '');
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 3145728) {
        formik.setFieldError('eventImage', "File size should be less than 3MB");
        formik.setFieldTouched('eventImage', true, false);
        return;
      }
      setSelectedFile(file);
      setIsCropperOpen(true); 
    }
  };

  const handleMultipleVenuesToggle = (hasMultiple: boolean) => {
    handleInputChange('hasMultipleVenues', hasMultiple);
  };

  const handleAddVenue = () => {
    setEditingVenue(undefined);
    setIsVenueModalOpen(true);
  };

  const handleEditVenue = (venue: VenueData) => {
    setEditingVenue(venue);
    setIsVenueModalOpen(true);
  };

  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    type: 'warning' as 'warning' | 'danger' | 'info' | 'success'
  });

  const closeConfirmationDialog = () => {
    setConfirmationDialog(prev => ({ ...prev, isOpen: false }));
  };

  const showConfirmationDialog = (
    title: string,
    message: string,
    onConfirm: () => void,
    type: 'warning' | 'danger' | 'info' | 'success' = 'warning'
  ) => {
    setConfirmationDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const handleDeleteVenue = (venueId: string | number) => {
    const venueToDelete = formData.venues.find(v => (v.id || (v as any).venueId) === venueId);
    const affectedTickets = (formData.tickets || []).filter(t => t.venueId === venueId);
    const affectedCount = affectedTickets.length;

    showConfirmationDialog(
      "Delete Venue",
      `Are you sure you want to delete the venue "${venueToDelete?.name || (venueToDelete as any)?.venueName}"?${
        affectedCount ? `\n\nThis will also remove ${affectedCount} ticket(s) associated with this venue.` : ""
      }`,
      () => {
        const updatedVenues = formData.venues.filter(v => (v.id || (v as any).venueId) !== venueId);
        handleInputChange("venues", updatedVenues);

        if (Array.isArray(formData.tickets)) {
          const filteredTickets = formData.tickets.filter(t => t.venueId !== venueId);
          handleInputChange("tickets" as any, filteredTickets);
        }

        closeConfirmationDialog();
      },
      "danger"
    );
  };

  const handleSaveVenue = (venue: VenueData) => {
    let updatedVenues;
    if (editingVenue) {
      updatedVenues = formData.venues.map(v =>
        (v.id || (v as any).venueId) === (venue.id || (venue as any).venueId) ? venue : v
      );
    } else {
      updatedVenues = [...formData.venues, venue];
    }
    handleInputChange('venues', updatedVenues);
    setIsVenueModalOpen(false);
  };

  useEffect(() => {
    const getCountries = async () => {
      try {
        const response = await getAllCountries();
        setCountries(response);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    getCountries();
  }, []);

  return (
    <div className={`step-tab-panel ${activeTab === 1 ? "active" : ""}`} style={{ display: activeTab === 1 ? "flex" : "none", flexDirection: "column", gap: 24 }}>
      
      {/* Card 1: Event Information */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <FileText size={14} color="#EE3F7C" /> Event Information
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label className="v3-label">Event Name *</label>
            <p className="d-block fs-12 text-muted" style={{ marginTop: 0, marginBottom: '8px' }}>
              See how your name appears on the event page and a list of all places where your event name will be used.
            </p>
            <input
              className={`v3-input ${isFieldInvalid(formik.touched, formik.errors, "eventName") ? "is-invalid" : ""}`}
              type="text"
              placeholder="Enter event name here"
              name="eventName"
              value={formik.values.eventName}
              onChange={(e) => {
                formik.setFieldValue("eventName", e.target.value);
                handleInputChange("eventName", e.target.value);
              }}
              onBlur={() => {
                formik.setFieldTouched("eventName", true);
              }}
            />
            {getFieldError(formik.touched, formik.errors, "eventName") && (
              <div className="invalid-feedback d-block mt-1">{getFieldError(formik.touched, formik.errors, "eventName")}</div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 250px), 1fr))", gap: 16 }}>
            <div>
              <label className="v3-label">Duration Structure</label>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  onClick={() => {
                    formik.setFieldValue("isSingleDayEvent", true);
                    handleInputChange("isSingleDayEvent", true);
                    formik.setFieldValue("endDate", "");
                    handleInputChange("endDate", "");
                  }}
                  style={{
                    flex: 1, padding: '8px 0', fontSize: 11, fontWeight: 700,
                    borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                    background: formik.values.isSingleDayEvent ? 'rgba(238, 63, 124, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: formik.values.isSingleDayEvent ? '1px solid #EE3F7C' : '1px solid rgba(255,255,255,0.08)',
                    color: formik.values.isSingleDayEvent ? '#fff' : 'rgba(255,255,255,0.45)',
                  }}
                >Single-Day</button>
                <button
                  type="button"
                  onClick={() => {
                    formik.setFieldValue("isSingleDayEvent", false);
                    handleInputChange("isSingleDayEvent", false);
                  }}
                  style={{
                    flex: 1, padding: '8px 0', fontSize: 11, fontWeight: 700,
                    borderRadius: 8, cursor: 'pointer', transition: 'all 0.15s',
                    background: !formik.values.isSingleDayEvent ? 'rgba(238, 63, 124, 0.15)' : 'rgba(255,255,255,0.04)',
                    border: !formik.values.isSingleDayEvent ? '1px solid #EE3F7C' : '1px solid rgba(255,255,255,0.08)',
                    color: !formik.values.isSingleDayEvent ? '#fff' : 'rgba(255,255,255,0.45)',
                  }}
                >Multi-Day</button>
              </div>
            </div>

            <div>
              <label className="v3-label">{formik.values.isSingleDayEvent ? 'Event Date *' : 'Start Date *'}</label>
              <CustomDatePicker
                value={formik.values.eventDate}
                onChange={(val) => {
                  formik.setFieldValue("eventDate", val);
                  handleInputChange("eventDate", val);
                }}
                placeholder="Select date"
              />
              {getFieldError(formik.touched, formik.errors, "eventDate") && (
                <div className="invalid-feedback d-block mt-1">{getFieldError(formik.touched, formik.errors, "eventDate")}</div>
              )}
            </div>

            {!formik.values.isSingleDayEvent && (
              <div>
                <label className="v3-label">End Date *</label>
                <CustomDatePicker
                  value={formik.values.endDate}
                  onChange={(val) => {
                    formik.setFieldValue("endDate", val);
                    handleInputChange("endDate", val);
                  }}
                  placeholder="Select end date"
                />
                {getFieldError(formik.touched, formik.errors, "endDate") && (
                  <div className="invalid-feedback d-block mt-1">{getFieldError(formik.touched, formik.errors, "endDate")}</div>
                )}
              </div>
            )}
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 250px), 1fr))", gap: 16 }}>
            <div>
              <label className="v3-label">Start Time *</label>
              <CustomTimePicker
                value={formik.values.eventTime}
                onChange={(val) => {
                  formik.setFieldValue("eventTime", val);
                  handleInputChange("eventTime", val);
                }}
                placeholder="Select start time"
              />
              {getFieldError(formik.touched, formik.errors, "eventTime") && (
                <div className="invalid-feedback d-block mt-1">{getFieldError(formik.touched, formik.errors, "eventTime")}</div>
              )}
            </div>

            <div>
              <label className="v3-label">End Time</label>
              <CustomTimePicker
                value={formik.values.eventEndTime}
                onChange={(val) => {
                  formik.setFieldValue("eventEndTime", val);
                  handleInputChange("eventEndTime", val);
                }}
                placeholder="Select end time"
              />
              {getFieldError(formik.touched, formik.errors, "eventEndTime") && (
                <div className="invalid-feedback d-block mt-1">{getFieldError(formik.touched, formik.errors, "eventEndTime")}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Card 2: Event Descriptions */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <FileText size={14} color="#EE3F7C" /> Event Descriptions
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label className="v3-label">Main Description *</label>
            <p className="d-block fs-12 text-muted" style={{ marginTop: 0, marginBottom: '8px' }}>
              Provide full details, schedule, or special instructions required to attend.
            </p>
            <ReactQuill
              className={`text-editor ${isFieldInvalid(formik.touched, formik.errors, "eventDescription") ? "is-invalid" : ""}`}
              value={formik.values.eventDescription}
              onChange={(value) => {
                formik.setFieldValue("eventDescription", value);
                handleInputChange("eventDescription", value);
              }}
              onBlur={() => formik.setFieldTouched("eventDescription", true)}
              theme="snow"
            />
            {getFieldError(formik.touched, formik.errors, "eventDescription") && (
              <div className="invalid-feedback d-block mt-2">{getFieldError(formik.touched, formik.errors, "eventDescription")}</div>
            )}
          </div>

          <div>
            <label className="v3-label">Short Description (SEO &amp; Cards) *</label>
            <p className="d-block fs-12 text-muted" style={{ marginTop: 0, marginBottom: '8px' }}>
              Summarize your event in a short sentence for search results and social cards.
            </p>
            <input
              className={`v3-input ${isFieldInvalid(formik.touched, formik.errors, "shortDescription") ? "is-invalid" : ""}`}
              type="text"
              placeholder="Enter short description here"
              name="shortDescription"
              value={formik.values.shortDescription}
              onChange={(e) => {
                formik.setFieldValue("shortDescription", e.target.value);
                handleInputChange("shortDescription", e.target.value);
              }}
              onBlur={() => formik.setFieldTouched("shortDescription", true)}
            />
            {getFieldError(formik.touched, formik.errors, "shortDescription") && (
              <div className="invalid-feedback d-block mt-1">{getFieldError(formik.touched, formik.errors, "shortDescription")}</div>
            )}
          </div>
        </div>
      </div>

      {/* Card 3: Event Category */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <Folder size={14} color="#EE3F7C" /> Event Category
        </div>
        <div>
          <label className="v3-label">Choose Category *</label>
          <p className="d-block fs-12 text-muted" style={{ marginTop: 0, marginBottom: '8px' }}>
            Categorize your event to help guests find it quickly.
          </p>
          <Select
            placeholder="Select Category"
            value={categoriesList.map(cat => ({ value: String(cat.categoryId), label: cat.categoryName })).find(opt => opt.value === formik.values.categories?.[0]) || null}
            onChange={(data: any) => {
              const updated = data ? [data.value] : [];
              formik.setFieldValue("categories", updated);
              handleInputChange("categories", updated);
            }}
            options={categoriesList.map(cat => ({ value: String(cat.categoryId), label: cat.categoryName }))}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            styles={{
              menuPortal: (base) => ({ ...base, zIndex: 99999 }),
              control: (provided, state) => ({
                ...provided,
                backgroundColor: "rgba(255,255,255,0.03)",
                borderColor: state.isFocused ? "#EE3F7C" : "rgba(255,255,255,0.08)",
                borderRadius: "12px",
                minHeight: "44px",
                boxShadow: "none",
                color: "#fff",
                "&:hover": { borderColor: "rgba(238, 63, 124, 0.4)" },
              }),
              input: (provided) => ({ ...provided, color: "#fff", fontSize: "13px" }),
              singleValue: (provided) => ({ ...provided, color: "#fff", fontSize: "13px" }),
              placeholder: (provided) => ({ ...provided, color: "rgba(255,255,255,0.4)", fontSize: "13px" }),
              menu: (provided) => ({
                ...provided,
                backgroundColor: "#12121c",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "14px",
                zIndex: 99999,
                padding: "6px",
                boxShadow: "0 20px 48px rgba(0, 0, 0, 0.7)",
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected ? "rgba(238, 63, 124, 0.2)" : state.isFocused ? "rgba(255,255,255,0.05)" : "transparent",
                color: "#fff",
                fontSize: "13px",
                borderRadius: "8px",
                cursor: "pointer",
              }),
            }}
          />
          {getFieldError(formik.touched, formik.errors, "categories") && (
            <div className="invalid-feedback d-block mt-2">{getFieldError(formik.touched, formik.errors, "categories")}</div>
          )}
        </div>
      </div>

      {/* Card 4: Event Cover Poster */}
      <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <div className="v3-section-head">
          <ImageIcon size={14} color="#EE3F7C" /> Event Cover Poster
        </div>
        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.45)", margin: 0 }}>
          Upload a high-resolution 3:4 portrait poster or banner image for your event page.
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 24, alignItems: "stretch", width: "100%" }}>
          <div style={{ width: 160, maxWidth: "100%", flexShrink: 0 }}>
            <div 
              onClick={() => fileInputRef.current?.click()} 
              style={{ 
                cursor: "pointer", borderRadius: 16, overflow: "hidden", 
                border: "2px dashed rgba(238,63,124,0.3)", width: "100%", aspectRatio: "3/4", 
                display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.02)",
                transition: "all 0.2s ease"
              }}
            >
              {formik.values.eventImage ? (
                <img
                  src={
                    typeof formik.values.eventImage === 'string'
                      ? formik.values.eventImage
                      : (formik.values.eventImage as any)?.url
                      ? (formik.values.eventImage as any).url
                      : (formik.values.eventImage as any) instanceof File || (formik.values.eventImage as any) instanceof Blob
                      ? URL.createObjectURL(formik.values.eventImage as any)
                      : ''
                  }
                  alt="Cover Preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <div style={{ textAlign: "center", padding: 16, display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                  <ImageIcon size={28} color="#EE3F7C" />
                  <span style={{ fontSize: 10, fontFamily: "monospace", color: "#fff", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 700 }}>Upload Poster</span>
                </div>
              )}
            </div>
            <input ref={fileInputRef} id="thumbImg" type="file" accept="image/jpeg, image/png" style={{ display: "none" }} onChange={handleImageUpload} />
          </div>

          <div style={{ flex: "1 1 300px", display: "flex" }}>
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", padding: 20, background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: 16, width: "100%", boxSizing: "border-box" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                <span style={{ fontSize: 10, fontFamily: "monospace", color: "#F79B59", textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600 }}>Poster Controls</span>
                {!formik.values.eventImage ? (
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="v3-btn-primary" style={{ fontSize: 12, padding: "10px 16px" }}>
                    <ImageIcon size={14} /> Upload Image
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" onClick={() => fileInputRef.current?.click()} className="v3-btn-secondary" style={{ fontSize: 11, flex: 1 }}>Replace Poster</button>
                    <button type="button" onClick={() => { formik.setFieldValue("eventImage", null); onInputChange("eventImage", null); }} className="v3-btn-secondary" style={{ fontSize: 11, flex: 1, color: "#FF5D73", borderColor: "rgba(255,93,115,0.2)" }}>Remove</button>
                  </div>
                )}
              </div>
              <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 12, marginTop: 16 }}>
                <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", fontStyle: "italic", marginBottom: 4 }}>Preferred 3:4 aspect ratio (Max: 3MB).</p>
              </div>
            </div>
          </div>
        </div>

        {getFieldError(formik.touched, formik.errors, "eventImage") && (
          <div className="invalid-feedback d-block mt-2">{getFieldError(formik.touched, formik.errors, "eventImage")}</div>
        )}

        {isCropperOpen && selectedFile && createPortal(
          <div className="cropper-modal-overlay">
            <div className="cropper-modal-card">
              <ImageCropper
                imageFile={selectedFile}
                aspect={3 / 4}
                onCancel={() => setIsCropperOpen(false)}
                onCropComplete={(croppedFile) => {
                  formik.setFieldValue("eventImage", croppedFile);
                  onInputChange("eventImage", croppedFile);
                  setIsCropperOpen(false);
                }}
              />
            </div>
          </div>,
          document.body
        )}
      </div>

      {/* Card 5: Venue & Location */}
      {!formData.isOnlineEvent && (
        <div className="glass-panel" style={{ display: "flex", flexDirection: "column", gap: 16, position: "relative", zIndex: 30 }}>
          <div className="v3-section-head">
            <MapPin size={14} color="#EE3F7C" /> Venue &amp; Location
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Multiple Venues</div>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.45)", margin: "4px 0 0 0" }}>Enable if your event takes place across multiple locations.</p>
            </div>
            <button
              type="button"
              className={`v3-toggle ${formData.hasMultipleVenues ? "on" : "off"}`}
              onClick={() => handleMultipleVenuesToggle(!formData.hasMultipleVenues)}
            >
              <span className="v3-toggle-knob" />
            </button>
          </div>

          {!formData.hasMultipleVenues ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))", gap: 16, marginTop: 8 }}>
              <div>
                <label className="v3-label">Venue Name *</label>
                <input
                  className="v3-input"
                  type="text"
                  placeholder="Enter venue name"
                  value={formData.venueDetails.venueName || (formData.venueDetails as any).name || ""}
                  onChange={(e) => onInputChange('venueDetails', { ...formData.venueDetails, venueName: e.target.value })}
                />
              </div>
              <div>
                <label className="v3-label">Event Location *</label>
                <GooglePlacesAutocomplete
                  apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ""}
                  selectProps={{
                    placeholder: "Search for a location or address...",
                    isClearable: true,
                    value: formData.eventLocation ? {
                      label: formData.eventLocation,
                      value: {
                        description: formData.eventLocation,
                        place_id: '',
                        geometry: { location: { lat: formData.venueDetails.latitude, lng: formData.venueDetails.longitude } }
                      }
                    } : null,
                    onChange: (place: any) => {
                      if (place) {
                        const placeDetails = {
                          name: place.label || "",
                          formattedAddress: place.label || "",
                          addressLine1: place.value?.description || "",
                          addressLine2: "", country: "", state: "", city: "", zipCode: "",
                          latitude: place.value?.geometry?.location?.lat,
                          longitude: place.value?.geometry?.location?.lng,
                        };

                        if (place.value?.place_id) {
                          const geocoder = new google.maps.Geocoder();
                          geocoder.geocode({ placeId: place.value.place_id }, (results, status) => {
                            if (status === "OK" && results && results[0]) {
                              const result = results[0];
                              const addressComponents = result.address_components;
                              const placeDetailsEnhanced = {
                                name: place.label || "",
                                formattedAddress: result.formatted_address || place.label || "",
                                addressLine1: result.formatted_address || place.value?.description || "",
                                addressLine2: "", country: "", state: "", city: "", zipCode: "",
                                latitude: result.geometry?.location?.lat(),
                                longitude: result.geometry?.location?.lng(),
                              };
                              addressComponents.forEach((component) => {
                                const types = component.types;
                                if (types.includes("country")) placeDetailsEnhanced.country = component.long_name;
                                else if (types.includes("administrative_area_level_1")) placeDetailsEnhanced.state = component.long_name;
                                else if (types.includes("locality")) placeDetailsEnhanced.city = component.long_name;
                                else if (types.includes("postal_code")) placeDetailsEnhanced.zipCode = component.long_name;
                                else if (types.includes("route")) placeDetailsEnhanced.addressLine1 = component.long_name;
                                else if (types.includes("street_number")) placeDetailsEnhanced.addressLine1 = component.long_name + " " + placeDetailsEnhanced.addressLine1;
                              });
                              handleSingleVenueLocationChange(placeDetailsEnhanced);
                            } else {
                              handleSingleVenueLocationChange(placeDetails);
                            }
                          });
                        } else {
                          handleSingleVenueLocationChange(placeDetails);
                        }
                      } else {
                        handleSingleVenueLocationChange({
                          name: '', formattedAddress: '', addressLine1: '', addressLine2: '',
                          country: '', state: '', city: '', zipCode: '', latitude: undefined, longitude: undefined,
                        });
                      }
                    },
                    styles: {
                      control: (provided: any, state: any) => ({
                        ...provided, backgroundColor: "rgba(255,255,255,0.03)", borderColor: state.isFocused ? "#EE3F7C" : "rgba(255,255,255,0.08)",
                        borderRadius: "12px", minHeight: "44px", boxShadow: "none", color: "#fff",
                      }),
                      input: (provided: any) => ({ ...provided, color: "#fff", fontSize: "13px" }),
                      singleValue: (provided: any) => ({ ...provided, color: "#fff", fontSize: "13px" }),
                      placeholder: (provided: any) => ({ ...provided, color: "rgba(255,255,255,0.4)", fontSize: "13px" }),
                      menu: (provided: any) => ({ ...provided, backgroundColor: "rgba(18, 18, 28, 0.98)", backdropFilter: "blur(24px)", borderRadius: "14px", zIndex: 99999 }),
                      option: (provided: any, state: any) => ({ ...provided, backgroundColor: state.isSelected ? "rgba(238, 63, 124, 0.2)" : state.isFocused ? "rgba(255,255,255,0.05)" : "transparent", color: "#fff" }),
                    },
                  }}
                />
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h6 style={{ margin: 0, color: "#fff" }}>Venues ({formData.venues.length})</h6>
                <button type="button" className="v3-btn-secondary" style={{ fontSize: 12 }} onClick={handleAddVenue}>+ Add Venue</button>
              </div>

              {formData.venues.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", background: "rgba(255,255,255,0.02)", borderRadius: 12 }}>
                  <p style={{ color: "rgba(255,255,255,0.45)", margin: 0, fontSize: 12 }}>No venues added yet. Click "+ Add Venue" to get started.</p>
                </div>
              ) : (
                <VenueList venues={formData.venues} onEditVenue={handleEditVenue} onDeleteVenue={handleDeleteVenue} />
              )}
            </div>
          )}
        </div>
      )}

      {/* Venue Modal */}
      <VenueModal
        isOpen={isVenueModalOpen}
        toggle={() => setIsVenueModalOpen(false)}
        onSubmit={handleSaveVenue}
        venue={editingVenue}
        countries={countries}
        eventStartDate={formData.eventDate}
        eventEndDate={formData.endDate}
        isSingleDayEvent={formData.isSingleDayEvent}
      />
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        toggle={closeConfirmationDialog}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        type={confirmationDialog.type}
        confirmText="Yes, Delete"
        cancelText="Cancel"
        icon="fa-solid fa-exclamation-triangle"
      />
    </div>
  );
};

export default EventDetailsTab;